import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import { createPortal } from "react-dom"
import { useSearchParams } from "react-router-dom"
import {
  Box, Button, Flex, HStack, IconButton, Input, Select, Text, Badge, Spinner, useToast, Wrap, WrapItem, Link, useBreakpointValue,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
} from "@chakra-ui/react"
import { FaCheck, FaTimes, FaUndo, FaHome, FaRoute, FaPlus, FaChevronLeft, FaChevronRight, FaDirections, FaMapMarkedAlt, FaWifi } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import { Panel } from "../../components/shared/Section"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { supplierLabel } from "../../tools/supplierLabel"
import { TriangleDownIcon } from "@chakra-ui/icons"
import SupplierChecklist from "./SupplierChecklist"
import * as outbox from "./outbox"
import { neutral, signal, type } from "../../theme/tokens"

/**
 * My route (Reps section — backend docs/michael/rep-field/TASK-rep-tracker.md).
 * Layout 2026-10-07 (Michael: "similar to the scorecard … routes on top in a row, the store a full page"):
 *  - the day's stops: a horizontal row on top on a phone, a list on the left on a tablet / computer;
 *  - the selected stop: a full store page — Visited / Missed, Navigate, a store comment, and one line per
 *    article (ranged YES or with stock there): code, description, ranged in store, SOH, a reason and a comment,
 *    saved per visit (field_visit_lines; reasons are placeholders "Line 1 …" until the real list arrives).
 * The day picker, the month strip, the unplanned visit and the "not in this month's route" list are unchanged.
 * An admin (master) sees any rep's route here too (rep picker; ?rep= from Rep Routing).
 */
const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")
const km = (v) => (v == null ? "–" : `${Number(v).toFixed(Number(v) < 10 ? 1 : 0)} km`)
const mins = (v) => (v == null ? "" : `${Math.round(Number(v))} min`)
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })
const shortDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-ZA", { weekday: "short", day: "numeric" })
const addDays = (d, n) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10) }
const shortName = (s) => String(s || "").replace(/^Dis-Chem\s+/i, "").replace(/\s+Pharmacy$/i, "")
// Every stop says which retailer it is (Michael 2026-10-07: "specify if that is a Dis-Chem, Clicks, Checkers store").
const RETAILER_LABEL = { DIS: "Dis-Chem", CHK: "Checkers", CLI: "Clicks", PNP: "Pick n Pay", MAK: "Makro" }
const retailerOf = (s) => RETAILER_LABEL[String(s?.retailer || "DIS").toUpperCase()] || String(s?.retailer || "")
const STATUS = {
  planned: { label: "To visit", scheme: "gray", dot: "gray.400" },
  visited: { label: "Visited", scheme: "green", dot: "green.500" },
  late: { label: "Visited (other day)", scheme: "teal", dot: "teal.500" },
  missed: { label: "Missed", scheme: "red", dot: "red.500" },
  additional: { label: "Additional visit", scheme: "purple", dot: "purple.500" },
}
const MAX_PHOTOS = 10
// Same as the scorecard: big JPEG / PNG / WebP shrunk in the browser to save mobile data; the server normalises all.
const preShrink = (file) => new Promise((resolve) => {
  if (!file || file.size < 600 * 1024 || !/^image\/(jpeg|png|webp)$/i.test(file.type)) return resolve(file)
  const url = URL.createObjectURL(file)
  const img = new window.Image()
  const done = (out) => { URL.revokeObjectURL(url); resolve(out || file) }
  img.onload = () => {
    try {
      const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
      if (scale >= 1) return done(file)
      const canvas = document.createElement("canvas")
      canvas.width = Math.round(img.naturalWidth * scale)
      canvas.height = Math.round(img.naturalHeight * scale)
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => done(blob && blob.size < file.size ? new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.8)
    } catch (e) { done(file) }
  }
  img.onerror = () => done(file)
  img.src = url
})

const where = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({})
  navigator.geolocation.getCurrentPosition((p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }), () => resolve({}), { timeout: 5000, maximumAge: 60000 })
})

// The store page's three views (Michael 2026-10-07): the rep's action list first.
// Order (Michael 2026-10-07): Ranged & stock first, then Ranged & no stock, then Not ranged, has stock.
const STOCK_VIEWS = [
  { key: "ok", label: "Ranged & stock", short: "In stock", test: (a) => a.ranged === "YES" && Number(a.soh) > 0 },
  { key: "gap", label: "Ranged & no stock", short: "No stock", test: (a) => a.ranged === "YES" && !(Number(a.soh) > 0) },
  { key: "off", label: "Not ranged, has stock", short: "Not ranged", test: (a) => a.ranged !== "YES" && Number(a.soh) > 0 },
]

// "Missed Thu 8 · catch-up" — a stop re-planned after the store was missed earlier this month (2026-10-07).
const missedTag = (s) => (s.missed_on ? `Missed ${shortDay(s.missed_on)} · catch-up` : null)

const Dot = ({ status }) => <Box as="span" display="inline-block" w="10px" h="10px" borderRadius="full" bg={(STATUS[status] || STATUS.planned).dot} flexShrink={0} />

// One stop in the phone's top row.
const StopPill = ({ s, active, onClick, joined = false }) => (
  <Button
    className="mr-pill" onClick={onClick} variant={active ? "solid" : "outline"} colorScheme={active ? "teal" : "gray"} flexShrink={0}
    h="auto" py={2} px={3} borderRadius="10px" borderBottomRadius={joined ? 0 : "10px"} textAlign="left" maxW="190px"
  >
    <Flex direction="column" align="flex-start" gap={0.5} minW={0}>
      <HStack spacing={1.5}><Dot status={s.status} /><Text fontSize={type.size.small} fontWeight={700}>{s.status === "additional" ? "+" : s.seq}</Text><Text fontSize={type.size.caption} fontWeight={600} opacity={0.8}>{retailerOf(s)}</Text></HStack>
      <Text fontSize={type.size.small} fontWeight={600} noOfLines={1} maxW="160px">{shortName(s.site_description)}</Text>
      {missedTag(s) && <Badge colorScheme="orange" fontSize="0.65em">{missedTag(s)}</Badge>}
    </Flex>
  </Button>
)

// One stop in the tablet / computer side list.
const StopRow = ({ s, active, onClick }) => (
  <Box
    as="button" onClick={onClick} w="100%" textAlign="left" p={3} mb={2} borderRadius="10px" border="1px solid"
    borderColor={active ? "teal.400" : neutral[200]} bg={active ? "teal.50" : neutral[0]} _hover={{ borderColor: "teal.300" }}
  >
    {s.status !== "additional" && s.leg_km != null && (
      <Text fontSize={type.size.caption} color={neutral[500]} mb={1}>{km(s.leg_km)} · {mins(s.leg_min)}</Text>
    )}
    <HStack spacing={2} align="flex-start">
      <Box pt="6px"><Dot status={s.status} /></Box>
      <Box minW={0}>
        <Text fontWeight={700} color={neutral[800]} fontSize={type.size.body} noOfLines={2}>{s.status === "additional" ? "+" : `${s.seq}.`} {retailerOf(s)} · {shortName(s.site_description)}</Text>
        <Text fontSize={type.size.caption} color={neutral[500]}>{(STATUS[s.status] || STATUS.planned).label}{s.visit_kind === "repeat" ? " · 2nd visit" : ""}</Text>
        {missedTag(s) && <Badge colorScheme="orange" mt={1}>{missedTag(s)}</Badge>}
      </Box>
    </HStack>
  </Box>
)

// One article line on the store page.
const ArticleLine = ({ a, reasons, onSave, readOnly = false, compact = false }) => {
  const [reason, setReason] = useState(a.reason || "")
  // Dis-Chem: the ranging file's letter instead of NO (Helena 2026-10-16) — D = mark it down, S = sell out the stock
  const ranged = a.ranged === "YES" ? { t: "YES", c: "green" }
    : a.ranged === "NO" && a.flag === "D" ? { t: "D", c: "red", tip: "D — discontinued: mark it down" }
    : a.ranged === "NO" && a.flag === "S" ? { t: "S", c: "orange", tip: "S — suspended at this store: sell out the stock" }
    : a.ranged === "NO" ? { t: "NO", c: "red" } : { t: "–", c: "gray" }
  const soh = a.soh == null ? null : Number(a.soh)
  const saveComment = (e) => { if (e.target.value !== (a.line_comment || "")) { a.line_comment = e.target.value; onSave(a, reason, e.target.value) } }
  // Phone / rep login: the Curaden Scorecard row (Helena 2026-10-13: "revise the rep layout to be like the curaden
  // scorecard") — name; code (teal) · supplier with the Ranged / SOH tiles right beside it; reason | comment on one
  // row; a hairline divider instead of a boxed card.
  if (compact) {
    const tile = (label, value, color, bg) => (
      <Box px={2} py="2px" borderRadius="7px" bg={bg} textAlign="center" minW="48px" lineHeight={1.15} flexShrink={0}>
        <Text fontSize="9px" fontWeight={600} letterSpacing=".05em" textTransform="uppercase" color={neutral[500]}>{label}</Text>
        <Text fontSize="15px" fontWeight={700} color={color}>{value}</Text>
      </Box>
    )
    const rangedColor = ranged.t === "YES" ? "green.700" : ranged.t === "S" ? "orange.600" : ranged.t === "NO" || ranged.t === "D" ? signal.down : neutral[400]
    const sohColor = soh == null ? neutral[400] : soh > 0 ? neutral[800] : signal.down
    return (
      <Box className="mr-line" py={2} borderBottom="1px solid" borderColor={neutral[200]}>
        <Text className="mr-line-name" fontWeight={700} fontSize="14px" lineHeight={1.2} color={neutral[800]}>{a.article_description}</Text>
        <Flex className="mr-line-meta" align="center" gap={2} mt={1}>
          <Text fontSize="12px" fontWeight={600} color="#0e7490" letterSpacing=".02em" whiteSpace="nowrap">{a.article_code}</Text>
          <Box title={ranged.tip || undefined}>{tile("Ranged", ranged.t, rangedColor, ranged.t === "NO" || ranged.t === "D" ? signal.downSoft : ranged.t === "S" ? "orange.50" : neutral[100])}</Box>
          {tile("SOH", soh == null ? "–" : soh, sohColor, soh != null && !(soh > 0) ? signal.downSoft : neutral[100])}
          <Text fontSize="11px" color={neutral[500]} noOfLines={1} minW={0}>{a.suppliercode}</Text>
        </Flex>
        <Flex className="mr-line-inputs" gap={2} mt="6px">
          <Select
            size="sm" h="36px" fontSize="14px" borderRadius="7px" flex="0 0 32%" minW={0} placeholder="Reason" textOverflow="ellipsis" value={reason} isDisabled={readOnly}
            onChange={(e) => { setReason(e.target.value); onSave(a, e.target.value, a.line_comment) }}
          >
            {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Input size="sm" h="36px" fontSize="14px" borderRadius="7px" flex="0 0 40%" minW={0} placeholder="Comment" defaultValue={a.line_comment || ""} isReadOnly={readOnly} onBlur={saveComment} />
        </Flex>
      </Box>
    )
  }
  return (
    <Box className="mr-article" p={3} mb={2} border="1px solid" borderColor={neutral[200]} borderRadius="10px" bg={neutral[0]}>
      <Flex className="mr-article-head" justify="space-between" align="flex-start" gap={3}>
        <Box minW={0}>
          <Text fontWeight={600} color={neutral[800]} fontSize={type.size.body}>{a.article_description}</Text>
          <Text fontSize={type.size.caption} color={neutral[500]}>{a.article_code} · {a.suppliercode}</Text>
        </Box>
        <Flex className="mr-article-facts" gap={4} flexShrink={0} align="center">
          <Box textAlign="center">
            <Text fontSize={type.size.caption} color={neutral[500]}>Ranged</Text>
            <Badge colorScheme={ranged.c} fontSize="0.85em" title={ranged.tip || undefined}>{ranged.t}</Badge>
          </Box>
          <Box textAlign="center" minW="44px">
            <Text fontSize={type.size.caption} color={neutral[500]}>SOH</Text>
            <Text fontSize="lg" fontWeight={700} color={soh == null ? neutral[400] : soh > 0 ? neutral[800] : signal.down}>{soh == null ? "–" : soh}</Text>
          </Box>
        </Flex>
      </Flex>
      <Flex gap={2} mt={2} direction={compact ? "column" : "row"}>
        <Select
          size="md" maxW={compact ? undefined : "200px"} placeholder="Reason…" value={reason} isDisabled={readOnly}
          onChange={(e) => { setReason(e.target.value); onSave(a, e.target.value, a.line_comment) }}
        >
          {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
        <Input
          size="md" flex={compact ? "none" : 1} w={compact ? "100%" : "auto"} placeholder="Comment" defaultValue={a.line_comment || ""} isReadOnly={readOnly}
          onBlur={(e) => { if (e.target.value !== (a.line_comment || "")) { a.line_comment = e.target.value; onSave(a, reason, e.target.value) } }}
        />
      </Flex>
    </Box>
  )
}

const MyRoute = () => {
  const toast = useToast()
  const [params] = useSearchParams()
  // selectedSupplier = the left-hand supplier picker: the store page shows that supplier's articles (Michael 2026-10-07).
  const { selectedOrg, isMaster, selectedSupplier: pickedSupplier, allSuppliersView, setAllSuppliersView, setSelectedSupplier, user_supplier_access } = useSupplierRetailer()
  // "All suppliers" (left-hand picker, this page only) = no supplier filter.
  const selectedSupplier = allSuppliersView ? "" : pickedSupplier
  const orgArg = useMemo(() => (isMaster ? { org_code: selectedOrg } : {}), [isMaster, selectedOrg])
  const [me, setMe] = useState(null)
  const [error, setError] = useState(null)
  const [rep, setRep] = useState(params.get("rep") || "")
  // Compact (phone) layout — theme/mobile.css `.mr-compact`: on a narrow viewport for everyone, and ALWAYS for a
  // rep login (a rep works from a phone in a store; a big phone or "desktop site" mode reports more than 768 px,
  // which left Helena on the laptop two-column layout — 2026-10-13).
  const phone = useBreakpointValue({ base: true, md: false }, { ssr: false }) === true
  const compact = phone || me?.role === "rep"
  const [date, setDate] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selId, setSelId] = useState(null)
  const [articles, setArticles] = useState(null) // {rows, reasons} | "loading"
  const [extra, setExtra] = useState("")
  // Store-page filters: a Blue Mango store carries ~700 articles over ~30 suppliers — too long for a phone
  // (2026-10-07). The supplier comes from the left-hand picker; search + "Ranged & stock" narrow it further.
  const [search, setSearch] = useState("")
  const [view, setView] = useState("ok")
  const [shown, setShown] = useState(50)
  // Keep the selected day visible in the swipeable strip (it can sit off-screen late in the month).
  const stripRef = useRef(null)
  useEffect(() => {
    const el = stripRef.current && date ? stripRef.current.querySelector(`[data-day="${date}"]`) : null
    if (el && el.scrollIntoView) { try { el.scrollIntoView({ block: "nearest", inline: "center" }) } catch (e) { /* old browser */ } }
  }, [date, data])

  // Phone / rep (Helena 2026-10-13): the day strip shows FIVE days; Prev / Today / Next sit in the top bar beside
  // the supplier picker — Next = the next five days, Prev = the previous five, Today = today. Past the month's
  // edge the next / previous month is loaded (the route API returns one month at a time).
  const WIN = 5
  const [winStart, setWinStart] = useState(0)
  const monthDays = useMemo(() => (data?.month || []).map((d) => d.plan_date), [data])
  useEffect(() => {
    if (!monthDays.length || !date) return
    let idx = monthDays.findIndex((d) => d >= date)
    if (idx < 0) idx = monthDays.length - 1
    setWinStart((cur) => (idx >= cur && idx < cur + WIN ? cur : Math.max(0, Math.min(idx, monthDays.length - WIN))))
  }, [monthDays, date])
  const monthEdge = (d, last) => { const x = new Date(`${d}T12:00:00Z`); if (last) { x.setUTCMonth(x.getUTCMonth() + 1, 1) } else { x.setUTCDate(1) } return x.toISOString().slice(0, 10) }
  // "today" = only today's box beside the buttons; "window" = five days (after Next / Prev). Helena 2026-10-13:
  // "when it says today it must only have today's little date box; if they click next, only the next 5 days".
  const [dayMode, setDayMode] = useState("today")
  const goToday = () => { setDayMode("today"); setDate(me?.today) }
  const pageDays = (dir) => {
    setDayMode("window")
    if (!monthDays.length) { setDate(addDays(date, dir * WIN)); return }
    let idx = monthDays.findIndex((d) => d >= date)
    if (idx < 0) idx = monthDays.length - 1
    let next = dayMode === "today" ? (dir > 0 ? idx + 1 : idx - WIN) : winStart + dir * WIN
    if (dir < 0 && next < 0 && (dayMode === "today" ? idx > 0 : winStart > 0)) next = 0
    if (next >= 0 && next < monthDays.length) { setWinStart(next); setDate(monthDays[next]); return }
    if (dir > 0) setDate(monthEdge(monthDays[monthDays.length - 1], true)) // first day of the next month
    else setDate(addDays(monthEdge(monthDays[0], false), -1)) // last day of the previous month
  }
  const [barSlot, setBarSlot] = useState(null)
  // The suppliers this stop's retailer has for the organisation (2026-10-16): the server sends them with the store's
  // articles — Curaden only at Clicks for Blue Mango, Caring Candies at Dis-Chem + Clicks, … Until the first answer
  // arrives the login's own list is used.
  const [stopSups, setStopSups] = useState([])
  const supList = stopSups.length ? stopSups : (user_supplier_access || [])
  // Supplier on the rep's store page: starts on the FIRST supplier of the list (not "All suppliers"), and a
  // "Next supplier" button walks the list (Helena 2026-10-13). "All suppliers" stays as the last option.
  const pickSup = (code) => {
    if (!code) { if (setAllSuppliersView) setAllSuppliersView(true); return }
    setSelectedSupplier(code)
    if (setAllSuppliersView) setAllSuppliersView(false)
  }
  const nextSup = () => {
    const list = supList.map((x) => x.value)
    if (!list.length) return
    const i = allSuppliersView ? -1 : list.indexOf(pickedSupplier)
    pickSup(list[(i + 1) % list.length])
  }
  const firstSupDone = useRef(false)
  useEffect(() => {
    if (firstSupDone.current || me?.role !== "rep" || !(user_supplier_access || []).length) return
    firstSupDone.current = true
    if (allSuppliersView) pickSup(user_supplier_access[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, user_supplier_access])
  useEffect(() => { setBarSlot(document.getElementById("sp-topbar-slot")) }, [])

  const post = useCallback((path, body = {}) => axios.post(`${API}/field/${path}`, { userid: uid(), ...orgArg, ...body }), [orgArg])
  // Every save goes through the offline queue (outbox.js, 2026-10-16): sent now, or kept on the phone until there is signal.
  // Unsent work (Helena 2026-10-16: a box "in case they forget to click Submit"): every save on a store — tick, comment,
  // reason, checklist, photo — marks that stop as changed since its last Submit; a sent Submit clears it.
  const unsent = useRef(new Set())
  const save = useCallback((path, body = {}, files = null) => {
    if (path !== "submit-visit" && body.id != null) unsent.current.add(body.id)
    return outbox.send(path, { userid: uid(), ...orgArg, ...body }, files)
  }, [orgArg])
  useEffect(() => {
    // Closing / reloading the page with unsent work: the browser's own "leave this page?" prompt.
    const warn = (e) => { if (unsent.current.size) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [])
  const [waiting, setWaiting] = useState(0)
  useEffect(() => outbox.onCount(setWaiting), [])
  const queuedToast = useCallback(() => toast({ title: "No signal — saved on the phone", description: "It is sent as soon as the phone has signal again.", status: "warning", duration: 3500 }), [toast])

  useEffect(() => {
    post("me").then((r) => {
      const m = r.data.data
      setMe(m)
      setDate(m.today)
      if (m.role === "rep") setRep(m.rep)
      else setRep((cur) => (cur && m.reps.includes(cur) ? cur : m.reps[0]))
    }).catch((e) => setError(e?.response?.data?.message || e.message))
  }, [post])

  const load = useCallback(() => {
    if (!rep || !date) return
    setLoading(true)
    post("route", { rep, date })
      .then((r) => setData(r.data.data))
      .catch((e) => toast({ title: "Could not load the route", description: e?.response?.data?.message || e.message, status: "error", isClosable: true }))
      .finally(() => setLoading(false))
  }, [rep, date, post, toast])
  useEffect(load, [load])

  const stops = useMemo(() => data?.stops || [], [data])
  // The open stop: kept across reloads of the same day; otherwise the first one still to visit.
  useEffect(() => {
    if (!stops.length) { setSelId(null); return }
    setSelId((cur) => (stops.some((s) => s.id === cur) ? cur : (stops.find((s) => s.status === "planned") || stops[0]).id))
  }, [stops])
  const sel = stops.find((s) => s.id === selId) || null
  // Opening another stop (tap, or Submit → next stop) starts it on the FIRST supplier of THAT store's list, so the rep
  // works the list from the top (Helena 2026-10-14 for Submit, 2026-10-16 for a tap). The store's own list only
  // arrives with its articles, so the open is flagged here and applied in the load below.
  const restartSup = useRef(false)
  const goStop = (id) => { restartSup.current = true; setSelId(id) }
  // Leaving a store that has unsent work (another stop, another day, + Add visit) asks first; otherwise goes straight on.
  const [leaving, setLeaving] = useState(null) // { stop, go }
  const guard = (go) => {
    if (sel && me?.canEdit && unsent.current.has(sel.id)) { setLeaving({ stop: sel, go }); return }
    go()
  }
  const openStop = (id) => { if (id === selId) return; guard(() => goStop(id)) }

  useEffect(() => {
    if (!sel?.site_code) { setArticles(null); return }
    setArticles("loading")
    setShown(50)
    post("store-stock", { site_code: sel.site_code, id: sel.id, supplier: selectedSupplier || null })
      .then((r) => {
        const rows = r.data.data || []
        const sups = r.data.suppliers || []
        setStopSups(sups)
        if (restartSup.current) {
          restartSup.current = false
          if (me?.role === "rep" && sups.length && (allSuppliersView || sups[0].value !== pickedSupplier)) { pickSup(sups[0].value); return }
        }
        // The picked supplier is not worked at this store's retailer → open the stop on its first supplier.
        if (me?.role === "rep" && sups.length && !allSuppliersView && !sups.some((x) => x.value === pickedSupplier)) { pickSup(sups[0].value); return }
        setArticles({ rows, reasons: r.data.reasons || [] })
        // Every store / supplier opens on Ranged & stock (Helena 2026-10-13: "it must always default to ranged & stock").
        setView("ok")
      })
      .catch(() => setArticles({ rows: [], reasons: [] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.id, post, selectedSupplier])

  const tick = async (stop, status) => {
    const pos = status === "visited" ? await where() : {}
    try {
      const r = await save("visit", { id: stop.id, status, at: new Date().toISOString(), ...pos })
      if (r.queued) { queuedToast(); setData((d) => d && { ...d, stops: d.stops.map((x) => (x.id === stop.id ? { ...x, status } : x)) }); return }
      if (r.data?.data?.replanned && status === "missed") toast({ title: "Store moved to a day in its area", description: "It shows as a catch-up on that day.", status: "info", duration: 4000 })
      load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    }
  }
  const saveComment = (stop, comment) => save("comment", { id: stop.id, comment }).then((r) => r.queued && queuedToast()).catch(() => {})
  // "Submit store" at the end of a store (Helena 2026-10-13: "we need some kind of submit button per store"). Every
  // reason / comment is already saved as it is entered; this marks the stop VISITED (time + phone location, the same
  // as the tick), confirms, and opens the next store still to visit. A store already visited just moves on.
  const [submitting, setSubmitting] = useState(false)
  // Checklist + photos PER SUPPLIER for the open visit (2026-10-16, Helena — Blue Mango's five tick questions; photos
  // per supplier as the merchandising proof). sv = {SUP: {q1..q5, photos, images}}; pendingPh = photos waiting for signal.
  const [sv, setSv] = useState({})
  const [questions, setQuestions] = useState([])
  const [pendingPh, setPendingPh] = useState({})
  const [uploading, setUploading] = useState(false)
  const loadChecks = useCallback((id) => {
    if (!id) { setSv({}); return }
    post("supplier-checks", { id }).then((r) => { setSv(r.data.data || {}); setQuestions(r.data.questions || []) }).catch(() => {})
  }, [post])
  useEffect(() => { loadChecks(sel?.id) }, [sel?.id, loadChecks])
  const onTickQ = async (q, value) => {
    const sup = selectedSupplier
    if (!sel || !sup) return
    setSv((m) => ({ ...m, [sup]: { ...(m[sup] || { photos: 0, images: [] }), [q]: value } }))
    try { const r = await save("supplier-tick", { id: sel.id, supplier: sup, q, value }); if (r.queued) queuedToast() } catch (e) {
      setSv((m) => ({ ...m, [sup]: { ...(m[sup] || {}), [q]: !value } }))
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    }
  }
  const onPhotosSup = async (list) => {
    const sup = selectedSupplier
    const files = Array.from(list || []).filter((x) => x && x.size > 0)
    if (!sel || !sup || !files.length) return
    const key = `${sel.id}|${sup}`
    const have = (sv[sup]?.photos || 0) + (pendingPh[key] || []).length
    const take = files.slice(0, Math.max(0, MAX_PHOTOS - have))
    if (take.length < files.length) toast({ title: `At most ${MAX_PHOTOS} photos per supplier per visit`, status: "warning", isClosable: true })
    if (!take.length) return
    setUploading(true)
    try {
      const prepared = []
      for (const x of take) { const blob = await preShrink(x); prepared.push({ blob, name: blob.name || x.name || "photo.jpg" }) }
      const r = await save("supplier-photos", { id: sel.id, supplier: sup }, prepared)
      if (r.queued) {
        queuedToast()
        setPendingPh((m) => ({ ...m, [key]: [...(m[key] || []), ...prepared.map((p) => ({ url: URL.createObjectURL(p.blob) }))] }))
      } else if (r.data?.data?.visit) {
        setSv((m) => ({ ...m, [sup]: { ...(m[sup] || {}), ...r.data.data.visit } }))
        toast({ title: `${take.length} photo${take.length === 1 ? "" : "s"} saved`, status: "success", duration: 2000 })
      }
    } catch (e) {
      toast({ title: "Photos not saved", description: e?.response?.data?.message || e.message, status: "error", duration: 8000, isClosable: true })
    } finally { setUploading(false) }
  }
  // Waiting saves go out when the signal is back; then the screen re-reads the route and the checklist.
  const selIdRef = useRef(null)
  selIdRef.current = sel?.id || null
  useEffect(() => {
    outbox.start((n) => {
      toast({ title: `Signal back — ${n} save${n === 1 ? "" : "s"} sent`, status: "success", duration: 3000 })
      setPendingPh({})
      load()
      loadChecks(selIdRef.current)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const submitStore = async (after = null) => {
    const stop = sel
    if (!stop || submitting) return
    setSubmitting(true)
    try {
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur() } catch (e) { /* noop */ }
    const order = stops.filter((x) => x.id !== stop.id && x.status === "planned")
    const nextStop = order.find((x) => (x.seq || 0) > (stop.seq || 0)) || order[0] || null
    if (stop.status === "planned" || stop.status === "missed") {
      const pos = await where()
      try { const q = await save("visit", { id: stop.id, status: "visited", at: new Date().toISOString(), ...pos }); if (q.queued) queuedToast() } catch (e) {
        toast({ title: "Not submitted", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
        return
      }
    }
    // Email the visit (Monique at Blue Mango on prod — FIELD_VISIT_EMAIL_TO on the server, 2026-10-13).
    // Photos are saved per supplier as they are taken (the checklist panel); Submit only emails the visit.
    let emailed = false
    try {
      const r = await save("submit-visit", { id: stop.id })
      emailed = !!r.data?.data?.email
      unsent.current.delete(stop.id)
    } catch (e) { /* the visit itself is saved */ }
    toast({ title: "Store submitted", description: `${stop.site_description}${emailed ? " · emailed" : ""}${after ? "" : nextStop ? ` — next: ${shortName(nextStop.site_description)}` : " — that was the last store today"}`, status: "success", duration: 3500 })
    // The next store starts again on its FIRST supplier (openStop); on the last store the current list restarts.
    if (after) after()
    else if (nextStop) goStop(nextStop.id)
    else if (supList[0]) pickSup(supList[0].value)
    load()
    try { window.scrollTo({ top: 0, behavior: "smooth" }) } catch (e) { /* old browser */ }
    } finally { setSubmitting(false) } // one email per press: the button is locked while a submit runs
  }
  const saveLine = (a, reason, comment) =>
    save("line", { id: sel.id, article_code: a.article_code, reason: reason || null, comment: comment || null })
      .then((r) => r.queued && queuedToast())
      .catch((e) => toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true }))
  const addExtra = async () => {
    if (!extra) return
    const pos = await where()
    try {
      await post("additional", { rep, site_code: extra, ...pos })
      setExtra("")
      if (date !== me.today) setDate(me.today); else load()
      toast({ title: "Additional visit logged", status: "success", duration: 3000 })
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    }
  }

  if (error) return <Panel><Text fontSize={type.size.small} color={signal.down}>{error}</Text></Panel>
  if (!me) return <Panel><Spinner size="sm" /></Panel>

  const planned = stops.filter((s) => s.status !== "additional")
  const done = stops.filter((s) => ["visited", "late", "additional"].includes(s.status)).length
  const isToday = date === me.today
  const dayBox = (d) => {
    const isSel = d.plan_date === date
    const isTodayDay = d.plan_date === me.today
    const allDone = d.done >= d.stores && d.stores > 0
    const look = isTodayDay
      ? { bg: isSel ? "orange.400" : "orange.50", color: isSel ? "white" : "orange.700", borderColor: "orange.400", borderWidth: "2px" }
      : isSel
        ? { bg: allDone ? "green.500" : "gray.600", color: "white", borderColor: "transparent" }
        : { bg: "white", color: allDone ? "green.700" : "gray.700", borderColor: allDone ? "green.300" : "gray.200" }
    return (
      <Button key={d.plan_date} data-day={d.plan_date} size="xs" h="32px" flexShrink={0} variant="outline" fontWeight={isSel || isTodayDay ? 700 : 500} {...look} _hover={{ opacity: 0.9 }} onClick={() => d.plan_date !== date && guard(() => setDate(d.plan_date))}>
        {shortDay(d.plan_date)} · {d.done}/{d.stores}
      </Button>
    )
  }
  const barDays = dayMode === "today"
    ? (data?.month || []).filter((d) => d.plan_date === date)
    : (data?.month || []).slice(winStart, winStart + WIN)
  const unrouted = (data?.assigned || []).filter((a) => a.not_routed)
  const rows = articles && articles !== "loading" ? articles.rows : []
  const q = search.trim().toLowerCase()
  const viewTest = (STOCK_VIEWS.find((v) => v.key === view) || STOCK_VIEWS[0]).test
  const visible = rows.filter((a) =>
    viewTest(a) &&
    (!q || String(a.article_description || "").toLowerCase().includes(q) || String(a.article_code).includes(q)))
  const st = sel ? STATUS[sel.status] || STATUS.planned : null

  return (
    <Box className={`mr-page${compact ? " mr-compact" : ""}`}>
      {waiting > 0 && (
        <Flex align="center" gap={2} mb={2} px={3} py={2} borderRadius="8px" bg="orange.50" border="1px solid" borderColor="orange.200" fontSize="13px" color="orange.800" fontWeight={600}>
          <FaWifi /> {waiting} save{waiting === 1 ? "" : "s"} waiting for signal, kept on this phone
        </Flex>
      )}
      {compact && barSlot && createPortal(
        <>
          <Button size="sm" h="32px" px={2} flexShrink={0} variant="outline" onClick={() => guard(() => pageDays(-1))}>Prev</Button>
          <Button size="sm" h="32px" px={2} flexShrink={0} variant="outline" onClick={() => guard(goToday)}>Today</Button>
          <Button size="sm" h="32px" px={2} flexShrink={0} variant="outline" onClick={() => guard(() => pageDays(1))}>Next</Button>
          <Flex ref={stripRef} gap={1} ml={1} minW={0} overflowX="auto" sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {barDays.length ? barDays.map(dayBox) : (
              <Text fontSize="12px" color={neutral[500]} whiteSpace="nowrap" px={1}>{date ? shortDay(date) : ""} · no route</Text>
            )}
          </Flex>
        </>,
        barSlot,
      )}
      <PageToolbar
        // A rep gets no "Blue Mango · my route" strip (Helena 2026-10-13: screen space) — the toolbar renders nothing.
        description={me.role === "rep" ? null : `${me.orgName} · rep routes`}
        filters={me.role === "admin" ? (
          <Select size="sm" w="auto" minW="220px" value={rep} onChange={(e) => setRep(e.target.value)}>
            {me.reps.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
        ) : null}
      />

      <Panel>
        {/* Phone / rep (Helena 2026-10-13: "squash prev / today / next and the dates into each other"): ONE row —
            ‹ · the date (tap = today) · › — instead of three buttons with the date on a line of its own. */}
        <Flex className="mr-daynav" align="center" justify="space-between" wrap="wrap" gap={2} mb={3} display={compact ? "none" : "flex"}>
          <HStack className="mr-daynav-buttons">
            <Button size="sm" variant="outline" leftIcon={<FaChevronLeft />} onClick={() => guard(() => setDate(addDays(date, -1)))}>Prev</Button>
            <Button size="sm" variant={isToday ? "solid" : "outline"} colorScheme="teal" onClick={() => guard(() => setDate(me.today))}>Today</Button>
            <Button size="sm" variant="outline" rightIcon={<FaChevronRight />} onClick={() => guard(() => setDate(addDays(date, 1)))}>Next</Button>
          </HStack>
          <Text className="mr-daynav-date" fontWeight={600} color={neutral[800]}>{date ? dayLabel(date) : ""}</Text>
        </Flex>

        {/* The month's days: one swipeable row on a phone, wrapped on a tablet / computer. */}
        {!compact && data?.month?.length > 0 && (
          <Flex align="center" gap={1} mb={compact ? 2 : 3}>
            <Flex ref={stripRef} className="mr-month" gap={1} flex="1" minW={0} wrap={compact ? "nowrap" : "wrap"} overflowX={compact ? "auto" : "visible"} pb={compact ? 1 : 0} sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              {(compact ? data.month.slice(winStart, winStart + WIN) : data.month).map((d) => {
                const isSel = d.plan_date === date
                const isTodayDay = d.plan_date === me.today
                const allDone = d.done >= d.stores && d.stores > 0
                // Today = bright teal ("highlight the block with today's date in a brighter colour"); the day being
                // looked at = solid; a fully-visited day = green.
                const look = isTodayDay
                  ? { bg: isSel ? "teal.400" : "teal.50", color: isSel ? "white" : "teal.700", borderColor: "teal.400", borderWidth: "2px" }
                  : isSel
                    ? { bg: allDone ? "green.500" : "gray.600", color: "white", borderColor: "transparent" }
                    : { bg: "white", color: allDone ? "green.700" : "gray.700", borderColor: allDone ? "green.300" : "gray.200" }
                return (
                  <Button key={d.plan_date} data-day={d.plan_date} size="xs" h={compact ? "32px" : undefined} flexShrink={0} variant="outline" fontWeight={isSel || isTodayDay ? 700 : 500} {...look} _hover={{ opacity: 0.9 }} onClick={() => d.plan_date !== date && guard(() => setDate(d.plan_date))}>
                    {shortDay(d.plan_date)} · {d.done}/{d.stores}
                  </Button>
                )
              })}
            </Flex>
          </Flex>
        )}

        {loading && !data ? <Spinner size="sm" /> : (
          <>
            <Flex className="mr-stats" gap={compact ? 3 : 4} wrap="wrap" fontSize={compact ? "13px" : type.size.small} color={neutral[600]} mb={compact ? 2 : 3}>
              <Text><b>{planned.length}</b> stores</Text>
              <Text><b>{done}</b> done</Text>
              <Text><b>{km(data?.day?.km)}</b>{compact ? " today" : " today incl. the drive home"}{data?.day?.km_source === "estimate" ? " (estimate)" : ""}</Text>
            </Flex>

            {stops.length === 0 ? (
              <Text fontSize={type.size.small} color={neutral[500]} mb={3}>No stores planned for this day.</Text>
            ) : (
              <>
                {/* Phone: the day's stops in one row on top (swipe). */}
                <Box display={compact ? "block" : "none"} mb={3}>
                  <HStack spacing={2} overflowX="auto" pb={2} sx={{ scrollbarWidth: "thin" }}>
                    {/* Each store with a small Map button under it — directions straight from the route (Helena 2026-10-13). */}
                    {stops.map((s) => (
                      <Flex key={s.id} direction="column" align="stretch" gap={0} flexShrink={0} maxW="190px">
                        <StopPill s={s} active={s.id === selId} onClick={() => openStop(s.id)} joined={s.lat != null} />
                        {s.lat != null && (
                          <Button as={Link} href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`} isExternal size="xs" h="26px" w="100%" mt="-1px" borderTopRadius={0} borderBottomRadius="10px" leftIcon={<FaMapMarkedAlt />} variant="outline" colorScheme="teal" bg="teal.50" _hover={{ textDecoration: "none", bg: "teal.100" }}>Map</Button>
                        )}
                      </Flex>
                    ))}
                    {/* HOME as the last block of the day (Helena 2026-10-13: "put home at the end of the stores, also in a block"). */}
                    <Box flexShrink={0} py={2} px={3} borderRadius="10px" border="1px dashed" borderColor={neutral[300]} bg={neutral[50]} maxW="200px" title={data?.home?.home_label || "No home address yet"}>
                      <HStack spacing={1.5}><FaHome color={neutral[600]} /><Text fontSize={type.size.small} fontWeight={700} color={neutral[800]}>HOME</Text>{planned.length > 0 && data?.day?.return_km != null && <Text fontSize={type.size.caption} color={neutral[500]}>{km(data.day.return_km)} back</Text>}</HStack>
                      <Text fontSize={type.size.small} fontWeight={600} color={data?.home?.home_label ? neutral[700] : signal.warn} noOfLines={1} maxW="176px">{data?.home?.home_label || "No home address yet"}</Text>
                    </Box>
                  </HStack>
                </Box>

                <Flex gap={4} align="flex-start">
                  {/* Tablet / computer: the stops on the left. */}
                  <Box display={compact ? "none" : "block"} w="270px" flexShrink={0}>
                    <Flex align="center" gap={2} mb={2}>
                      <FaHome color={neutral[500]} />
                      <Box>
                        <Text fontWeight={600} fontSize={type.size.small}>HOME</Text>
                        <Text fontSize={type.size.caption} color={neutral[500]}>{data?.home?.home_label || "No home address yet"}</Text>
                      </Box>
                    </Flex>
                    {stops.map((s) => <StopRow key={s.id} s={s} active={s.id === selId} onClick={() => openStop(s.id)} />)}
                    {planned.length > 0 && (
                      <Flex align="center" gap={2} mt={1}>
                        <FaHome color={neutral[500]} />
                        <Text fontSize={type.size.small} color={neutral[600]}><b>HOME</b> · {km(data?.day?.return_km)} back</Text>
                      </Flex>
                    )}
                  </Box>

                  {/* The store page. */}
                  {sel && (
                    <Box flex="1" minW={0}>
                      <Box className="mr-store-head" p={4} mb={3} border="1px solid" borderColor={neutral[200]} borderRadius="12px" bg={neutral[25]}>
                      {/* 2026-10-16 (Helena): the supplier checklist in the empty space next to the store name; on a phone it
                          drops under the supplier row (flex wrap). */}
                      <Flex className="mr-head-grid" gap={4} align="flex-start" wrap="wrap">
                      <Box flex="1 1 460px" minW={0}>
                        {sel.status !== "additional" && sel.leg_km != null && (
                          <Text fontSize={type.size.caption} color={neutral[500]} mb={1}><FaRoute style={{ display: "inline", marginRight: 4 }} />{km(sel.leg_km)} · {mins(sel.leg_min)} from the previous stop</Text>
                        )}
                        {/* Phone / rep: Visited · Missed · Navigate as square icon buttons NEXT TO the store name
                            (Helena 2026-10-13) — the labelled row below is the laptop layout. */}
                        <Flex align="flex-start" gap={2} wrap={compact ? "wrap" : "nowrap"}>
                          <Text className="mr-store-title" flex={compact ? "0 1 auto" : "1"} minW={0} fontSize="xl" fontWeight={700} color={neutral[800]} lineHeight="short">{sel.status === "additional" ? "+" : `${sel.seq}.`} {sel.site_description}</Text>
                          {compact && (
                            <HStack spacing="6px" flexShrink={0}>
                              {!me.canEdit ? null : sel.status === "planned" || sel.status === "missed" ? (
                                <>
                                  <IconButton aria-label="Visited" title="Visited" icon={<FaCheck />} colorScheme="green" size="sm" w="40px" h="40px" onClick={() => tick(sel, "visited")} />
                                  {sel.status === "planned" && <IconButton aria-label="Missed" title="Missed" icon={<FaTimes />} variant="outline" colorScheme="red" size="sm" w="40px" h="40px" onClick={() => tick(sel, "missed")} />}
                                </>
                              ) : sel.status !== "additional" && sel.source !== "georep" ? (
                                <IconButton aria-label="Undo" title="Undo" icon={<FaUndo />} variant="outline" size="sm" w="40px" h="40px" onClick={() => tick(sel, "planned")} />
                              ) : null}
                              {sel.lat != null && (
                                <IconButton as={Link} aria-label="Navigate" title="Navigate" icon={<FaDirections />} variant="outline" colorScheme="teal" size="sm" w="40px" h="40px" href={`https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}`} isExternal />
                              )}
                            </HStack>
                          )}
                          {/* Store comment next to the store name, after the tick / cross / navigate (Helena 2026-10-13). */}
                          {compact && (
                            <Input
                              key={`cc-${sel.id}`} size="sm" h="40px" flex="1 1 110px" minW="110px" maxW="260px" fontSize="14px" borderRadius="7px" bg={neutral[0]} placeholder="Store comment" isReadOnly={!me.canEdit}
                              defaultValue={sel.comment || ""} onBlur={(e) => e.target.value !== (sel.comment || "") && saveComment(sel, e.target.value)}
                            />
                          )}
                        </Flex>
                        <HStack spacing={2} mt={1} wrap="wrap">
                          <Badge colorScheme="blue">{retailerOf(sel)}</Badge>
                          <Text fontSize={type.size.small} color={neutral[500]}>{sel.site_code}</Text>
                          {sel.visit_kind === "repeat" && <Badge colorScheme="purple">2nd visit</Badge>}
                          {missedTag(sel) && <Badge colorScheme="orange">{missedTag(sel)}</Badge>}
                          <Badge colorScheme={st.scheme}>{st.label}</Badge>
                          {sel.source === "georep" && <Badge variant="outline">Georep</Badge>}
                        </HStack>
                        {/* Phone / rep: the supplier picker lives HERE, under the store name and above the articles, bright so
                            it is found (Helena 2026-10-13: "change that colour … move that down above the articles"). */}
                        {compact && (
                          <Flex className="mr-supplier-row" mt={2} gap="6px" align="center">
                            <Select
                              className="mr-supplier" flex="1 1 0" minW="90px" size="sm" h="36px" fontSize="12px" fontWeight={700} borderRadius="7px" pl={2}
                              bg="teal.500" color="white" borderColor="teal.500" iconColor="white" icon={<TriangleDownIcon />} iconSize="14px" _hover={{ bg: "teal.600" }}
                              value={allSuppliersView ? "" : pickedSupplier || ""}
                              onChange={(e) => pickSup(e.target.value)}
                              sx={{ "> option": { color: "black", background: "white" } }}
                            >
                              {supList.map((x) => <option key={x.value} value={x.value}>{x.text && x.text.trim().toUpperCase() !== String(x.value).toUpperCase() ? x.text : supplierLabel(x.value, user_supplier_access)}</option>)}
                              <option value="">All suppliers</option>
                            </Select>
                            {/* Step through the suppliers without opening the list (Helena 2026-10-13). */}
                            {supList.length > 1 && (
                              <Button size="sm" h="36px" px={2} flexShrink={0} fontSize="13px" variant="outline" colorScheme="teal" bg="white" rightIcon={<FaChevronRight />} iconSpacing={1} onClick={nextSup}>Next supplier</Button>
                            )}
                            {/* Submit the store (Helena 2026-10-13: "next to the next supplier, nice colour"): marks it visited,
                                emails the visit, opens the next store. */}
                            {me.canEdit && sel.source !== "georep" && (
                              <Button size="sm" h="36px" px={2} flexShrink={0} fontSize="13px" iconSpacing={1} colorScheme="green" bg="green.500" color="white" fontWeight={700} leftIcon={<FaCheck />} _hover={{ bg: "green.600" }} isLoading={submitting} loadingText="Submit" onClick={() => submitStore()}>Submit</Button>
                            )}
                          </Flex>
                        )}
                        <Flex className="mr-actions" gap={2} mt={3} wrap="wrap" justify="space-between" align="center">
                          <Flex className="mr-visit-buttons" gap={2} wrap="wrap" display={compact ? "none !important" : "flex"}>
                          {!me.canEdit ? null : sel.status === "planned" || sel.status === "missed" ? (
                            <>
                              <Button size="md" colorScheme="green" leftIcon={<FaCheck />} onClick={() => tick(sel, "visited")}>Visited</Button>
                              {sel.status === "planned" && <Button size="md" variant="outline" colorScheme="red" leftIcon={<FaTimes />} onClick={() => tick(sel, "missed")}>Missed</Button>}
                            </>
                          ) : sel.status !== "additional" && sel.source !== "georep" ? (
                            <Button size="md" variant="ghost" leftIcon={<FaUndo />} onClick={() => tick(sel, "planned")}>Undo</Button>
                          ) : null}
                          {sel.lat != null && (
                            <Button as={Link} className="mr-nav" size="md" variant="outline" colorScheme="teal" leftIcon={<FaDirections />} href={`https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}`} isExternal _hover={{ textDecoration: "none" }}>Navigate</Button>
                          )}
                          </Flex>
                          {/* The article views, on the right of the visit buttons (Michael 2026-10-07: less space). */}
                          {articles !== "loading" && rows.length > 0 && (
                            <Flex className="mr-views" gap={1} wrap="wrap" justify={compact ? "flex-start" : "flex-end"}>
                              {STOCK_VIEWS.map((v) => (
                                <Button
                                  key={v.key} size="sm" colorScheme={view === v.key ? "red" : "gray"} variant={view === v.key ? "solid" : "outline"} bg={view === v.key ? undefined : neutral[0]}
                                  onClick={() => { setView(v.key); setShown(50) }}
                                >
                                  {v.label} ({rows.filter(v.test).length})
                                </Button>
                              ))}
                            </Flex>
                          )}
                        </Flex>
                        <Input
                          key={`c-${sel.id}`} className="mr-store-comment" display={compact ? "none" : undefined} size="md" mt={3} bg={neutral[0]} placeholder="Store comment" isReadOnly={!me.canEdit}
                          defaultValue={sel.comment || ""} onBlur={(e) => e.target.value !== (sel.comment || "") && saveComment(sel, e.target.value)}
                        />
                      </Box>
                      {sel.source !== "georep" && questions.length > 0 && (
                        <SupplierChecklist
                          supplier={selectedSupplier || null}
                          label={selectedSupplier ? supplierLabel(selectedSupplier, user_supplier_access) : ""}
                          questions={questions} state={sv[selectedSupplier]} pending={pendingPh[`${sel.id}|${selectedSupplier}`] || []}
                          canEdit={me.canEdit} onTick={onTickQ} onPhotos={onPhotosSup} uploading={uploading}
                          body={{ userid: uid(), ...orgArg }} maxPhotos={MAX_PHOTOS}
                        />
                      )}
                      </Flex>
                      </Box>

                      {/* Phone / rep (Helena 2026-10-13: "search article code — make that much smaller"): the count and a
                          short search box share ONE line; the laptop keeps the heading + full-width search below. */}
                      {compact && (
                        <Flex className="mr-artbar" align="center" justify="space-between" gap={2} mb={2}>
                          <Text fontSize="13px" fontWeight={700} color={neutral[800]} noOfLines={1} minW={0}>
                            Articles{articles !== "loading" && rows.length > 0 ? ` · ${rows.filter((a) => a.ranged === "YES").length} ranged` : ""}
                          </Text>
                          {articles !== "loading" && rows.length > 0 && (
                            <Input size="sm" h="32px" flex="0 1 55%" minW="90px" fontSize="14px" borderRadius="7px" placeholder="Search" value={search} onChange={(e) => { setSearch(e.target.value); setShown(50) }} />
                          )}
                        </Flex>
                      )}
                      <Flex justify="space-between" align="baseline" mb={2} wrap="wrap" gap={2} display={compact ? "none" : "flex"}>
                        <Text fontWeight={700} color={neutral[800]}>Articles in this store · {selectedSupplier || "All suppliers"}</Text>
                        {articles !== "loading" && rows.length > 0 && (
                          <Text fontSize={type.size.caption} color={neutral[500]}>
                            {rows.filter((a) => a.ranged === "YES").length} ranged
                            {rows[0]?.as_at ? ` · stock as at ${String(rows[0].as_at).slice(0, 10)}` : ""}
                          </Text>
                        )}
                      </Flex>
                      {!compact && articles !== "loading" && rows.length > 0 && (
                        <Flex gap={2} mb={3} wrap="wrap" align="center">
                          <Input size="md" flex="1" minW="180px" placeholder="Search article or code" value={search} onChange={(e) => { setSearch(e.target.value); setShown(50) }} />
                        </Flex>
                      )}
                      {articles === "loading" ? <Spinner size="sm" /> : rows.length === 0 ? (
                        <Text fontSize={type.size.small} color={neutral[500]}>No ranged or stocked articles{selectedSupplier ? ` for ${selectedSupplier}` : ""} in this store — pick another supplier {compact ? "at the top" : "on the left"}.</Text>
                      ) : visible.length === 0 ? (
                        <Text fontSize={type.size.small} color={neutral[500]}>No articles match.</Text>
                      ) : (
                        <>
                          {visible.slice(0, shown).map((a) => <ArticleLine key={`${sel.id}|${a.article_code}`} a={a} reasons={articles.reasons} onSave={saveLine} readOnly={!me.canEdit} compact={compact} />)}
                          {visible.length > shown && (
                            <Button w="100%" variant="outline" onClick={() => setShown((n) => n + 50)}>Show more ({visible.length - shown} left)</Button>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Flex>
              </>
            )}

            {me.canEdit && <Flex className="mr-extra" gap={2} mt={4} wrap="wrap" align="center">
              <Select size="sm" maxW="420px" placeholder="Log a visit that was not planned (today)…" value={extra} onChange={(e) => setExtra(e.target.value)}>
                {(data?.assigned || []).map((a) => <option key={a.site_code} value={a.site_code}>{a.site_code} · {a.site_description}</option>)}
              </Select>
              <Button size="sm" leftIcon={<FaPlus />} onClick={() => guard(addExtra)} isDisabled={!extra}>Add visit</Button>
            </Flex>}

            {/* Photos moved into the supplier checklist next to the store name (2026-10-16): one set per supplier. */}

            {unrouted.length > 0 && (
              <Box className="mr-unrouted" mt={4}>
                <Text fontSize={type.size.caption} color={neutral[500]} mb={1}>Not in this month's route:</Text>
                <Wrap spacing={1}>
                  {unrouted.map((a) => (
                    <WrapItem key={a.site_code}>
                      <Badge variant="subtle" colorScheme={a.not_routed === "no stock" ? "orange" : "gray"} whiteSpace="normal">{a.site_code} · {a.site_description} — {a.not_routed}</Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
          </>
        )}
      </Panel>
      <Modal isOpen={!!leaving} onClose={() => setLeaving(null)} isCentered size={{ base: "xs", sm: "md" }}>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader fontSize="17px" pb={1}>You haven't sent {leaving ? shortName(leaving.stop.site_description) : ""} yet</ModalHeader>
          <ModalBody fontSize="14px" color={neutral[700]}>
            <Text>You made changes at this store but didn't press Submit.</Text>
            <Text mt={2} color={neutral[500]} fontSize="13px">If you continue without sending, your entries stay saved, but the visit email isn't sent.</Text>
          </ModalBody>
          <ModalFooter flexDirection="column" alignItems="stretch" gap={2}>
            <Button colorScheme="green" bg="green.500" leftIcon={<FaCheck />} isLoading={submitting}
              onClick={() => { const l = leaving; setLeaving(null); submitStore(l.go) }}>Submit and continue</Button>
            <Button variant="outline" onClick={() => { const l = leaving; setLeaving(null); unsent.current.delete(l.stop.id); l.go() }}>Continue without sending</Button>
            <Button variant="ghost" onClick={() => setLeaving(null)}>Stay here</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default MyRoute
